import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';

import {
  REASON_DOCUMENTS_BLOCK_ID,
  getReasonDocumentBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmFormDocument';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { IconInfo } from 'UIPackage/IconsModule/components/IconInfo';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './ErknmReasonsAttachments.module.scss';
import { ErknmReasonsAttachment } from './ErknmReasonsAttachment';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';

interface IErknmReasonsAttachments {
  watchOnly: boolean;
  isUnscheduledType: boolean;
  typeCode: string | undefined;
}

interface IErknmReasonsAttachmentsStoreProps {
  formRef: IKnmFormRef | undefined;
}

const ErknmReasonsAttachmentsWithoutStore = ({
  watchOnly,
  isUnscheduledType,
  typeCode,
  formRef,
}: IErknmReasonsAttachments & IErknmReasonsAttachmentsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addReasonDocument = useCallback(() => {
    if (!watchOnly && push) {
      const addingReasonDocument: IKnmFormDocument = {
        active: null,
        attachments: undefined,
        violationEvidences: undefined,
        correlationToken: null,
        description: null,
        guid: null,
        isNew: true,
        listId: uuidv4(),
        type: undefined,
      };

      push('reasonDocuments', addingReasonDocument);
    }
  }, [push, watchOnly]);

  const makeRemoveReasonDocumentHandler = useCallback(
    (i: number) => () => !watchOnly && remove && remove('reasonDocuments', i),
    [remove, watchOnly],
  );

  return (
    <FieldBlock
      label={
        <>
          {typeCode === KnmTypeCodesEnum.VP ? 'Документы-основания проведения КНМ' : 'Основания включения в план'}
          <ErknmFieldMark fieldName="reasonDocuments" />
        </>
      }
      id={REASON_DOCUMENTS_BLOCK_ID}
      color="black"
      fillInProcess={isUnscheduledType && <ErknmFillInProcessMessage name="reasonDocuments" />}
      button={
        <span className="shared-no-wrap-text">
          <Button
            className="shared-btn-add"
            text="Добавить документ"
            variant="link"
            color="primary"
            type="button"
            id="erknmReasonsAttachmentsAddButton"
            disabled={watchOnly}
            onClick={addReasonDocument}
          />
          <Tooltip
            overlayClassName={styles.DropdownTooltip}
            placement="top"
            content={
              <div className={styles.DropdownTooltipContent}>
                Для согласования проверки необходимо прикрепить документы, содержащие информацию об основании проведения
                проверки
              </div>
            }
          >
            <span>
              <IconInfo className={styles.DropdownTooltipIcon} />
            </span>
          </Tooltip>
        </span>
      }
      className="shared-field"
    >
      <FieldArray name="reasonDocuments">
        {({ fields }: FieldArrayRenderProps<IKnmFormDocument, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((reasonDocumentFieldName, sIndex) => {
                const fieldValue: IKnmFormDocument = fields.value?.[sIndex];
                const id = getReasonDocumentBlockId(fieldValue.listId);

                return (
                  <ErknmReasonsAttachment
                    key={id}
                    id={id}
                    reasonDocumentFieldName={reasonDocumentFieldName}
                    watchOnly={watchOnly}
                    index={sIndex}
                    makeRemoveReasonDocumentHandler={makeRemoveReasonDocumentHandler}
                    formRef={formRef}
                    type={fieldValue.type}
                  />
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmReasonsAttachmentsStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const ErknmReasonsAttachments = connect(mapStateToProps)(ErknmReasonsAttachmentsWithoutStore);
